import type { FullComposeViewState } from 'owa-calendar-compose-types';
import hasCustomTimeZones from '../utils/hasCustomTimeZones';
import { FullComposeFormViewType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import createAccCheckerViewState from 'owa-editor-acc-checker/lib/utils/createAccCheckerViewState';
import createEmojiPickerViewState from 'owa-editor-emoji-picker-plugin/lib/utils/createEmojiPickerViewState';
import createRibbonViewState from 'owa-editor-ribbonplugin/lib/utils/createRibbonViewState';
import createEditorViewState from 'owa-editor/lib/utils/createEditorViewState';
import createProofingViewState from 'owa-editor-proofing-plugin/lib/utils/createProofingViewState';
import { createAddinViewState } from 'owa-editor-addin-plugin-types/lib/utils/createAddinViewState';
import createInfoBarHostViewState from 'owa-info-bar/lib/utils/createInfoBarHostViewState';
import createAtMentionsViewState from 'owa-editor-mentions-picker-plugin/lib/utils/createAtMentionsViewState';
import createTopicViewState from 'owa-editor-topic-picker-plugin/lib/utils/createTopicViewState';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { getIsDarkTheme } from 'owa-fabric-theme';
import getExpressionId from 'owa-expressions-store/lib/utils/getExpressionId';
import { createPasteOptionViewState } from 'owa-editor-paste-option-plugin';
import createInlineImageViewStateV2 from 'owa-editor-inlineimage-plugin/lib/utils/createInlineImageViewState';
import { getDefaultShouldShortenEventDuration } from 'owa-calendar-cloud-settings';
import createDockingViewState from 'owa-editor-docking-plugin/lib/utils/createDockingViewState';
import { createAugloopSession } from 'augloop-service/lib/utils/createAugloopSession';
import { hasVisibleContent } from 'owa-calendar-forms-utils/lib/utils/hasVisibleContent';
import EditorScenarios from 'owa-editor/lib/store/schema/EditorScenarios';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { createCharmSuggestionsViewState } from 'owa-calendar-charm-suggestions-data';
import createAugLoopViewState from 'owa-editor-augloop-plugin/lib/utils/createAugLoopViewState';
import createSonoraMentionsViewState from 'owa-editor-sonora-mentions-picker-plugin/lib/utils/createSonoraMentionsViewState';
import { SonoraScenario } from 'owa-editor-sonora-types';

const MIN_BODY_HEIGHT = 72;
const COMPOSE_SCHEDULING_GRID_SCENARIO_ID = 'ComposeSchedulingGridScenario';
const COMPACT_WELL_THRESHOLD = 10;

// These are temporary values for A/b/n experiment to render the compact recipient well
// with >5 attendees or >3 attendees against the default of 10.
const COMPACT_WELL_THRESHOLD_FIVE = 5;
const COMPACT_WELL_THRESHOLD_THREE = 3;

export default function createComposeViewState(
    event: CalendarEvent,
    isCharmExplicitlySet: boolean,
    viewType: FullComposeFormViewType,
    isAcceptPNT: boolean,
    extractionSourceId: string,
    internetMessageId?: string,
    collabSpaceViewStateId?: string,
    createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps,
    formId?: string
): FullComposeViewState {
    let attendeeCount: number = event.RequiredAttendees?.length + event.OptionalAttendees?.length;
    let proofingViewState = createProofingViewState();

    let viewState: FullComposeViewState = {
        ...createInfoBarHostViewState(),
        docking: createDockingViewState(MIN_BODY_HEIGHT, true /*allowStickyDocking*/),
        dropViewState: createDropViewState(),
        isCharmExplicitlySet: isCharmExplicitlySet,
        freeBusyData: undefined,
        mentionsPicker: createAtMentionsViewState(null),
        topicPicker: createTopicViewState(),
        attachmentWell: undefined,
        bodyViewState: createEditorViewState(
            EditorScenarios.CalendarCompose,
            event.Body ? event.Body.Value : ''
        ),
        isOptionalAttendeeWellEnabled: event.OptionalAttendees?.length > 0,
        shouldFocusOptionalAttendeeWellOnMount: false,
        viewType: viewType,
        ribbon: createRibbonViewState(),
        isRibbonShown: false,
        showExpandedEditor:
            event.HasAttachments || (event.Body && hasVisibleContent(event.Body.Value)),
        emojiPicker: createEmojiPickerViewState(),
        accChecker: createAccCheckerViewState(false /*useFlexPane*/),
        inlineImageV2: createInlineImageViewStateV2(),
        customRecurrenceViewState: {
            recurrence: null,
            startDate: null,
        },
        subjectViewState: {
            selectionStart: 0,
            selectionEnd: 0,
        },
        currentSidePaneType: SidePaneType.None,
        showSchedulingAssistant: false,
        showCompactRecipientWell: attendeeCount > getThresholdForCompactRecipientWell(),
        showDateSelectorTimeZones: hasCustomTimeZones(event.Start.tz, event.End.tz),
        isUndoNotificationShown: false,
        schedulingGridScenarioId: formId ?? COMPOSE_SCHEDULING_GRID_SCENARIO_ID,
        currentSaveRequestTimeStamp: null,
        isAcceptProposeNewTime: isAcceptPNT,
        extractionSourceId: extractionSourceId,
        internetMessageId: internetMessageId,
        awaitingServiceResponse: false,
        unresolvedLocation: null,
        linkFiles: [],
        addin: createAddinViewState(event.AppendOnSend),
        uiEnabled: true,
        isCompactRecipientWellExpanded: false,
        isDarkMode: getIsDarkTheme(),
        typeOfItemBeingDragged: null,
        showDropHint: false,
        proofing: proofingViewState,
        isOnlineMeetingExplicitlySet: false,
        wasOnlineMeetingEverAutoSet: false,
        expressionId: formId ?? getExpressionId(),
        pasteOption: createPasteOptionViewState(),
        shouldAdjustStartTimeEndTime:
            getDefaultShouldShortenEventDuration() ||
            viewType == FullComposeFormViewType.DeeplinkPopOut,
        augloopSession: createAugloopSession(),
        isAllDayEventExplicitlySet: false,
        collabSpaceViewStateId: collabSpaceViewStateId,
        createCollabSpaceOnInitProps: createCollabSpaceOnInitProps,
        charmSuggestionsViewState: createCharmSuggestionsViewState(),
        augLoop: createAugLoopViewState(proofingViewState.editorProofingId),
        sonoraMentions: createSonoraMentionsViewState(SonoraScenario.CalendarCompose),
    };

    // Show 1 item first then it will recalculate
    viewState.ribbon.visibleItemCount = 1;

    return viewState;
}

let getThresholdForCompactRecipientWell = () => {
    if (isFeatureEnabled('cal-compactRecipientsWellThresholdThreeAttendees')) {
        return COMPACT_WELL_THRESHOLD_THREE;
    } else if (isFeatureEnabled('cal-compactRecipientsWellThresholdFiveAttendees')) {
        return COMPACT_WELL_THRESHOLD_FIVE;
    } else {
        return COMPACT_WELL_THRESHOLD;
    }
};
